import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { DropdownPopover } from '@/components';
import { stripPersistableSearch } from '@/config/persisted-query-params/stripPersistableSearch';
import { SELECTED_PRODUCTION_ORDER_PARAM } from '@/constants';
import { useFeatureFlags } from '@/contexts';
import { useCachedRegionSeason, useTrackEvents } from '@/hooks';

import SelectRegion from '../SelectRegion';

import { CropSeasonOptions } from './components';
import { useGetCropSeasonOptions } from './hooks';

type State = {
  region?: string;
  season?: string;
};

/**
 * Changes the region/season scope; it does not publish it. The route is the
 * scope's source of truth, so consumers resolve it from the URL and keep
 * working when this control — which renders inside the topbar MFE's custom
 * slot — is late or never mounts.
 */
const SelectSeason = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { season: selectedCropSeason, region: selectedRegion } = useParams();
  const { isEnabled } = useFeatureFlags();
  const isSearchTreePopoverEnabled = isEnabled(
    'releaseWebTopbarSearchTreeSelectors'
  );
  const [state, setState] = useState<State>({
    region: selectedRegion,
    season: selectedCropSeason
  });
  const { options, isLoading } = useGetCropSeasonOptions({
    regionId: state.region
  });
  // A missing region disables the query and leaves no options to display.
  const isLoadingSeasons = isLoading || !state.region;
  const { setValue: setCachedSeason } = useCachedRegionSeason();
  const trackEvent = useTrackEvents();

  const handleRegionOpenChange = useCallback(
    (open: boolean) => {
      trackEvent(
        open ? 'Click to open region selector' : 'Close region selector'
      );
    },
    [trackEvent]
  );
  const handleSeasonOpenChange = useCallback(
    (open: boolean) => {
      trackEvent(
        open
          ? 'Click to open crop season selector'
          : 'Close crop season selector'
      );
    },
    [trackEvent]
  );
  const handleSeasonCancel = useCallback(() => {
    trackEvent('Click to cancel crop season selection');
  }, [trackEvent]);
  const handleSeasonShowMore = useCallback(() => {
    trackEvent('Click to show more inactive crop seasons');
  }, [trackEvent]);

  const handleRegionChange = useCallback(
    (region: string) => {
      setState({
        region,
        season: undefined
      });
      if (isSearchTreePopoverEnabled) {
        trackEvent('Click to change region', {
          regionId: region
        });
      }
    },
    [isSearchTreePopoverEnabled, trackEvent]
  );

  const handleSeasonChange = useCallback(
    (season: string) => {
      const { region } = state;
      if (!region) {
        return;
      }

      // Replace the scope segment while retaining the current page path.
      const newPath = location.pathname.replace(
        /\/region\/[^/]+\/season\/[^/]+/,
        () => `/region/${region}/season/${season}`
      );

      // Drop the previous scope's persisted query params so they don't apply —
      // and get re-cached — under the new region/season. The gate restores the
      // new scope's own saved state from the bag instead.
      const search = stripPersistableSearch(location.search, [
        SELECTED_PRODUCTION_ORDER_PARAM
      ]);

      void navigate(`${newPath}${search}${location.hash}`, {
        replace: true
      });
      setCachedSeason({
        region,
        season
      });
      if (isSearchTreePopoverEnabled) {
        trackEvent('Click to apply crop season', {
          regionId: region,
          cropSeasonId: season
        });
      }
    },
    [
      state,
      setCachedSeason,
      navigate,
      location,
      trackEvent,
      isSearchTreePopoverEnabled
    ]
  );

  useEffect(() => {
    setState({
      region: selectedRegion,
      season: selectedCropSeason
    });
  }, [selectedCropSeason, selectedRegion]);

  if (isSearchTreePopoverEnabled) {
    return (
      <>
        <SelectRegion
          value={state.region}
          activeRegion={selectedRegion}
          onChange={handleRegionChange}
          onOpenChange={handleRegionOpenChange}
        />
        <CropSeasonOptions
          options={options}
          currentValue={state.season}
          isLoading={isLoadingSeasons}
          onConfirm={handleSeasonChange}
          onOpenChange={handleSeasonOpenChange}
          onCancel={handleSeasonCancel}
          onShowMore={handleSeasonShowMore}
        />
      </>
    );
  }

  return (
    <>
      <SelectRegion value={state.region} onChange={handleRegionChange} />
      <DropdownPopover
        isLoading={isLoadingSeasons}
        options={isLoadingSeasons ? [] : options}
        currentValue={state.season}
        onConfirm={handleSeasonChange}
        label='Select a Crop Season'
        title='Crop Season'
      />
    </>
  );
};

export default SelectSeason;
